import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api/api.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { AppBLL } from 'src/app/app.bll';
@Injectable({
  providedIn: 'root',
})
export class RefereeContactsBLL {
  //#region Loaders Observables

  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  readonly isLoading$: Observable<boolean> = this.isLoading.asObservable();

  private isAddContactModelOpen: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly isAddContactModelOpen$: Observable<boolean> =
    this.isAddContactModelOpen.asObservable();

  private isUpdateContactModelOpen: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly isUpdateContactModelOpen$: Observable<boolean> =
    this.isUpdateContactModelOpen.asObservable();


  private contacts: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly contacts$: Observable<any> = this.contacts.asObservable();


  private contact: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly contact$: Observable<any> = this.contact.asObservable();

  private creation_status: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);
  readonly creation_status$: Observable<string> =
    this.creation_status.asObservable();

  private contactError: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  readonly contactError$: Observable<string> = this.contactError.asObservable();

  selectedContact$: Subject<any> = new Subject<any>();
  selectedReferees$: Subject<any> = new Subject<any>();

  //#endregion Data Observables

  constructor(
    private api: ApiService,
    private toast: ToastService,
    private appBll: AppBLL
  ) { }

  public getContacts(admin_id: string = null) {
    this.isLoading.next(true);

    let url = `field-events?type=get_all_referee_contacts`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    this.api
      .get(url)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.contacts.next(response);
          this.isLoading.next(false);
        },
        (err) => {
          this.isLoading.next(false);
        }
      );
  }

  public getContact() {
    this.isLoading.next(true);

    let url = `field-events?type=get_referee_contact`;

    this.api
      .get(url)
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          this.contact.next(response);
          this.isLoading.next(false);
        },
        (err) => {
          this.isLoading.next(false);
        }
      );
  }

  public async createContact(contact, admin_id: string = null): Promise<boolean> {
    this.isLoading.next(true);
    this.contactError.next(null);

    let url = `field-events?type=post_referee_contact`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    try {
      const response: any = await this.api.post(url, contact).pipe(take(1)).toPromise();
      this.creation_status.next('success');
      this.toast.showToast('Success!', 'Contact has been created.');
      this.toggleAddContactModel(false);
      this.selectedReferees$.next(response?.data?.id);
      return true;
    } catch (err) {
      this.contactError.next(err?.error?.message || 'Unable to create the contact. Please try again.');
      return false;
    } finally {
      this.isLoading.next(false);
    }
  }

  // Resolves { added, skipped } (skipped rows carry row/email/reason), or null
  // when the whole upload was rejected.
  public async bulkCreateContacts(contacts: any[], admin_id: string = null): Promise<{ added: number; skipped: any[] } | null> {
    this.isLoading.next(true);

    let url = `field-events?type=post_bulk_referee_contacts`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    try {
      const response: any = await this.api.post(url, { contacts }).pipe(take(1)).toPromise();
      return { added: response?.added ?? 0, skipped: response?.skipped ?? [] };
    } catch (err) {
      this.toast.showToast('Error', err?.error?.message || 'Failed to upload contacts. Please check the file and try again.');
      return null;
    } finally {
      this.isLoading.next(false);
      this.getContacts(admin_id);
    }
  }

  public clearContactError() {
    this.contactError.next(null);
  }



  public async deleteContact(id: string, admin_id: string = null) {
    let url = `field-events?type=delete_referee_contact&id=${id}`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    this.api.delete(url).subscribe(
      async () => {
        this.getContacts(admin_id);
        this.toast.showToast('Success!', 'Contact has been deleted.');
      },
      async (err) => {
        console.log('HTTP Error', err);
      }
    );
  }

  // Same contract as createContact: resolves true on success, otherwise the
  // backend message is published on contactError$.
  public async editContact(id, contact, admin_id: string = null): Promise<boolean> {
    let url = `field-events?type=update_referee_contact`;

    if (admin_id) {
      url += `&admin_id=${admin_id}`;
    }

    this.isLoading.next(true);
    this.contactError.next(null);

    let update_contact = { ...contact, id: id };

    try {
      await this.api.patch(url, update_contact).pipe(take(1)).toPromise();
      this.toast.showToast('Success!', 'Contact has been updated.');
      this.creation_status.next('success');
      return true;
    } catch (err) {
      this.contactError.next(err?.error?.message || 'Unable to update the contact. Please try again.');
      return false;
    } finally {
      this.isLoading.next(false);
    }
  }



  toggleAddContactModel(isOpen: boolean) {
    // A fresh open shouldn't carry the previous attempt's error.
    if (isOpen) {
      this.contactError.next(null);
    }
    this.isAddContactModelOpen.next(isOpen);
  }

  changeUpdateContactModelOpen(isOpen: boolean) {
    this.isUpdateContactModelOpen.next(isOpen);
  }

}
